import { Box, Stack } from '@mui/material';
import { Button, colors, Tabs, Typography } from '@sweep-io/sweep-design';
import { useCallback, useEffect } from 'react';
import { BestPractice } from './types';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import NewBestPracticeCard from './NewBestPracticeCard';
import BestPracticesList from './BestPracticesList';
import RestrictedTooltip from '../common/permissions/RestrictedTooltip';
import { usePermissions } from '../../permissions/usePermissions';
import { useBestPracticesContent } from './useBestPracticesContent';
import { AgentType } from '@server/ai';
import { AiAgent } from '../aiAgentCenter/aiAgentsConsts';
import useAiAgents from '../aiAgentCenter/useAiAgents';
import useSendBiEvent from '../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../services/events';

type BestPracticesContentProps = {
  agentType?: AgentType;
  onCreateNew?: () => void;
  onPristineStateChange?: (isPristine: boolean) => void;
  agent?: AiAgent | null;
  isAgentCreationContext?: boolean;
  selectedBestPracticeIds?: string[];
  onBestPracticesChange?: (bestPracticeIds: string[]) => void;
  crmOrgId: string;
};

const BestPracticesContent = ({
  agentType,
  onCreateNew,
  onPristineStateChange,
  agent,
  isAgentCreationContext = false,
  selectedBestPracticeIds = [],
  onBestPracticesChange,
  crmOrgId,
}: BestPracticesContentProps) => {
  const {
    activeTab,
    setActiveTab,
    isCreatingNew,
    createNewCardRef,
    applicableDomain,
    isLoading,
    sweepBestPractices,
    customBestPractices,
    isCustomEmpty,
    isSweepEmpty,
    closeNewBestPracticeCard,
    isPristine,
    handleNewCardIsPristineChange,
    handleEditableCardIsPristineChange,
    handleCreateNew,
  } = useBestPracticesContent(agentType, agent, isAgentCreationContext);
  const sendBiEvent = useSendBiEvent();

  const { addAgentBestPractices } = useAiAgents();
  const { evaluatePermissions } = usePermissions();
  const [isAllowedCreate] = evaluatePermissions([{ permission: 'create:best-practices', crmOrgId }]);

  const handleBestPracticeCreated = useCallback(
    async (createdBestPractice: BestPractice) => {
      sendBiEvent({ name: AI_AGENTS_EVENTS.monitoringAgentNewBestPractice });
      if (isAgentCreationContext && onBestPracticesChange) {
        // In agent creation context: add to local selection so it gets included when agent is created
        const updatedSelection = [...selectedBestPracticeIds, createdBestPractice.id];
        onBestPracticesChange(updatedSelection);
      } else if (agent) {
        // In normal mode: directly associate with existing agent
        await addAgentBestPractices(agent.id, [createdBestPractice.id]);
      }
    },
    [
      sendBiEvent,
      isAgentCreationContext,
      onBestPracticesChange,
      agent,
      selectedBestPracticeIds,
      addAgentBestPractices,
    ],
  );

  // Notify parent of pristine state changes
  useEffect(() => {
    onPristineStateChange?.(isPristine);
  }, [isPristine, onPristineStateChange]);

  const getCreateNewButton = useCallback(
    (text: string) => (
      <RestrictedTooltip
        validation={{ type: 'explicit', allowed: isAllowedCreate }}
        notAllowedTitle="To create a new custom best practice, please contact your admin."
      >
        <Button
          variant="primary"
          size="tiny"
          onClick={onCreateNew ?? handleCreateNew}
          startIconName="Plus"
          disabled={!isAllowedCreate}
        >
          {text}
        </Button>
      </RestrictedTooltip>
    ),
    [handleCreateNew, isAllowedCreate, onCreateNew],
  );

  const renderBestPractices = useCallback(
    (bestPractices: BestPractice[], isCustom: boolean = false) => {
      //empty states:
      if (!isCustom && isSweepEmpty) {
        return (
          <Typography variant="body" color={colors.grey[700]}>
            No best practices available.
          </Typography>
        );
      }
      if (isCustom && isCustomEmpty && !isCreatingNew) {
        return (
          <Stack height="100%" justifyContent="center" alignItems="center" gap={2}>
            <Stack gap={1} justifyContent="center" alignItems="center">
              <Typography variant="h4-medium" color={colors.grey[800]}>
                No custom best practices yet
              </Typography>
              <Typography variant="body" color={colors.grey[700]}>
                Create custom best practices that Sweep will use when scanning your org
              </Typography>
            </Stack>
            {getCreateNewButton('Create your first best practice')}
          </Stack>
        );
      }

      return (
        <BestPracticesList
          bestPractices={bestPractices}
          isCustom={isCustom}
          onIsPristineChange={handleEditableCardIsPristineChange}
          button={isCustom ? getCreateNewButton('Create New') : undefined}
          agent={agent}
          isAgentCreationContext={isAgentCreationContext}
          selectedBestPracticeIds={selectedBestPracticeIds}
          onBestPracticesChange={onBestPracticesChange}
          crmOrgId={crmOrgId}
        >
          {isCustom && isCreatingNew && applicableDomain && (
            <NewBestPracticeCard
              ref={createNewCardRef}
              applicableDomain={applicableDomain}
              closeCard={closeNewBestPracticeCard}
              onIsPristineChange={handleNewCardIsPristineChange}
              onCreate={handleBestPracticeCreated}
              agent={agent}
            />
          )}
        </BestPracticesList>
      );
    },
    [
      isSweepEmpty,
      isCustomEmpty,
      isCreatingNew,
      handleEditableCardIsPristineChange,
      getCreateNewButton,
      agent,
      applicableDomain,
      createNewCardRef,
      closeNewBestPracticeCard,
      handleNewCardIsPristineChange,
      handleBestPracticeCreated,
      isAgentCreationContext,
      selectedBestPracticeIds,
      onBestPracticesChange,
      crmOrgId,
    ],
  );

  return (
    <Stack sx={{ flex: 1, overflow: 'hidden' }}>
      <Box
        sx={{
          p: '0 16px',
          position: 'relative',
          '.MuiTabs-root': {
            borderBottom: `1px solid ${colors.grey[120]}`,
          },
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value as 'custom' | 'sweep')}
          tabs={[
            {
              value: 'sweep',
              label: 'Recommended',
              badgeLabel:
                sweepBestPractices && sweepBestPractices.length > 0
                  ? sweepBestPractices.length.toString()
                  : undefined,
            },
            {
              value: 'custom',
              label: 'Custom',
              badgeLabel:
                customBestPractices && customBestPractices.length > 0
                  ? customBestPractices.length.toString()
                  : undefined,
            },
          ]}
        />
      </Box>

      <Stack sx={{ flex: 1, overflow: 'hidden', p: '16px 0' }}>
        {isLoading ? (
          <CenteredCircularProgress minHeight="200px" />
        ) : (
          <Stack gap={2} sx={{ flex: 1, overflow: 'hidden' }}>
            <Stack sx={{ flex: 1, overflow: 'auto', padding: '0 16px' }}>
              {activeTab === 'custom' && renderBestPractices(customBestPractices ?? [], true)}
              {activeTab === 'sweep' && renderBestPractices(sweepBestPractices ?? [])}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default BestPracticesContent;
